import { useState, useRef, useEffect } from 'react';
import { useLang } from '../lib/lang';
import { useAuth } from '../lib/auth';
import { adminUpsertTextOverride, adminDeleteTextOverride, adminTranslate } from '../lib/api';
import { Pencil, Sparkles, Save, RotateCcw, X, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

/**
 * Wraps any text node with an inline-edit pencil that appears when:
 *   - User is admin
 *   - Edit mode is enabled (toggled from header)
 *
 * Usage:
 *   <InlineEditable tKey="heroTitle1" as="span">{t('heroTitle1')}</InlineEditable>
 *
 * Props:
 *   tKey      (required) i18n key to override
 *   as        HTML element to render (default: 'span')
 *   children  current rendered text (must be a string node)
 *   className optional class for the wrapper
 *   multiline optional - render large textarea instead of single line input
 */
export default function InlineEditable({ tKey, as = 'span', children, className = '', multiline = false, ...props }) {
  const { editMode, lang, getDefault, overrides, refreshOverrides, setOverrideLocal } = useLang();
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const showPencil = isAdmin && editMode;
  const Tag = as;

  // If an admin saved an override for this key, prefer it over the children
  // (some texts come from admin settings — e.g. footer address/email — and
  // are NOT funneled through t(), so InlineEditable must apply the override
  // itself so edits reflect on the page immediately).
  // We fall back across languages: current lang → other lang → children.
  // This way if the admin only saved one language, the edit is still visible.
  const ov = overrides[tKey] || {};
  const otherLang = lang === 'fr' ? 'en' : 'fr';
  const overrideValue =
    (ov[lang] && ov[lang].trim()) ||
    (ov[otherLang] && ov[otherLang].trim()) ||
    '';
  const displayContent = overrideValue || children;

  // When edit mode is on, clicking ANYWHERE on the wrapped text opens the dialog.
  // We disable native links/buttons inside via pointer-events:none on children
  // (only while editing), so clicks always reach the editable wrapper.
  const handleClick = (e) => {
    if (!showPencil) return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <>
      <Tag
        onClick={handleClick}
        role={showPencil ? 'button' : undefined}
        tabIndex={showPencil ? 0 : undefined}
        onKeyDown={(e) => { if (showPencil && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(true); } }}
        className={`${className} ${showPencil ? 'relative inline-block group/edit outline outline-2 outline-dashed outline-gold/40 outline-offset-[3px] rounded-sm hover:outline-gold hover:outline-solid hover:bg-gold/10 cursor-pointer transition-colors' : ''}`}
        title={showPencil ? `Cliquez pour éditer · ${tKey}` : undefined}
        data-testid={showPencil ? `editable-${tKey}` : undefined}
        {...props}
      >
        {displayContent}
        {showPencil && (
          <span
            aria-hidden="true"
            className="absolute -top-3 -right-3 z-50 w-6 h-6 rounded-full bg-gold text-rolex-green shadow-lg flex items-center justify-center opacity-100 pointer-events-none"
          >
            <Pencil className="w-3 h-3" />
          </span>
        )}
      </Tag>
      {open && (
        <EditDialog
          tKey={tKey}
          multiline={multiline}
          currentLang={lang}
          getDefault={getDefault}
          overrides={overrides}
          fallbackText={typeof children === 'string' ? children : (typeof displayContent === 'string' ? displayContent : '')}
          onClose={() => setOpen(false)}
          onSaved={(fr, en) => {
            setOverrideLocal(tKey, fr, en);
            refreshOverrides();
          }}
        />
      )}
    </>
  );
}

function EditDialog({ tKey, multiline, currentLang, getDefault, overrides, fallbackText = '', onClose, onSaved }) {
  // Initial values: existing override if any, otherwise default i18n value,
  // otherwise the current visible text (for keys not present in i18n.js such as
  // contactAddress / openingHours / contactEmail which come from admin settings).
  const ov = overrides[tKey] || {};
  const [valueFr, setValueFr] = useState(ov.fr || getDefault(tKey, 'fr') || fallbackText || '');
  const [valueEn, setValueEn] = useState(ov.en || getDefault(tKey, 'en') || fallbackText || '');
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(null); // 'fr-to-en' | 'en-to-fr' | null

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminUpsertTextOverride({
        key: tKey,
        value_fr: valueFr,
        value_en: valueEn,
      });
      toast.success('Texte sauvegardé / Saved');
      onSaved(valueFr, valueEn);
      onClose();
    } catch (e) {
      toast.error('Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restaurer le texte par défaut ? L\u2019override sera supprimé.')) return;
    setSaving(true);
    try {
      await adminDeleteTextOverride(tKey);
      toast.success('Texte par défaut restauré');
      onSaved('', '');
      onClose();
    } catch (e) {
      toast.error('Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoTranslate = async (direction) => {
    // direction = 'fr-to-en' | 'en-to-fr'
    const isFrToEn = direction === 'fr-to-en';
    const source = isFrToEn ? 'fr' : 'en';
    const target = isFrToEn ? 'en' : 'fr';
    const text = isFrToEn ? valueFr : valueEn;
    if (!text || !text.trim()) {
      toast.error(isFrToEn ? 'Champ FR vide' : 'EN field is empty');
      return;
    }
    setTranslating(direction);
    try {
      const res = await adminTranslate(text, source, target, `WINLUXOR UI label, key="${tKey}"`);
      const translated = res.data?.translation || '';
      if (isFrToEn) setValueEn(translated);
      else setValueFr(translated);
      toast.success(res.data?.cached ? 'Traduit (cache)' : 'Traduit avec GPT-5-mini');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erreur de traduction';
      if (e?.response?.status === 402) {
        toast.error('OpenAI: crédit épuisé. Vérifiez votre solde.');
      } else {
        toast.error(msg);
      }
    } finally {
      setTranslating(null);
    }
  };

  const InputComp = multiline ? Textarea : Input;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl bg-cream border-2 border-gold/40">
        <DialogHeader>
          <DialogTitle className="text-rolex-green flex items-center gap-2">
            <Pencil className="w-5 h-5 text-gold-dark" />
            Éditer le texte
          </DialogTitle>
          <DialogDescription className="text-rolex-green/65 text-xs font-mono">
            Clé : <span className="text-gold-dark">{tKey}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* French field */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-rolex-green flex items-center gap-2">
                <span className="text-base">🇫🇷</span> Français
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={translating !== null || !valueEn?.trim()}
                onClick={() => handleAutoTranslate('en-to-fr')}
                className="text-xs h-7 border-gold/40 text-gold-dark hover:bg-gold/10"
                data-testid="translate-en-to-fr"
              >
                {translating === 'en-to-fr' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Traduire EN → FR
              </Button>
            </div>
            <InputComp
              value={valueFr}
              onChange={(e) => setValueFr(e.target.value)}
              className="bg-white border-gold/30 text-rolex-green focus-visible:ring-gold"
              rows={multiline ? 4 : undefined}
              placeholder="Texte en français…"
              data-testid="edit-input-fr"
            />
          </div>

          {/* English field */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-rolex-green flex items-center gap-2">
                <span className="text-base">🇬🇧</span> English
              </Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={translating !== null || !valueFr?.trim()}
                onClick={() => handleAutoTranslate('fr-to-en')}
                className="text-xs h-7 border-gold/40 text-gold-dark hover:bg-gold/10"
                data-testid="translate-fr-to-en"
              >
                {translating === 'fr-to-en' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Translate FR → EN
              </Button>
            </div>
            <InputComp
              value={valueEn}
              onChange={(e) => setValueEn(e.target.value)}
              className="bg-white border-gold/30 text-rolex-green focus-visible:ring-gold"
              rows={multiline ? 4 : undefined}
              placeholder="English text…"
              data-testid="edit-input-en"
            />
          </div>

          <p className="text-[11px] text-rolex-green/55 italic">
            💡 Astuce : laissez un champ vide pour utiliser le texte par défaut dans cette langue.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 flex flex-row justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            disabled={saving || !overrides[tKey]}
            className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
            data-testid="reset-default"
          >
            <RotateCcw className="w-4 h-4" />
            Restaurer défaut
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="cancel-edit">
              <X className="w-4 h-4" />
              Annuler
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving} className="bg-gold-gradient text-rolex-green font-bold hover:opacity-90" data-testid="save-edit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Sauvegarder
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
